import { Component, Inject, OnInit } from '@angular/core';
import { NotificationService } from '../../../../shared/services/notification.service';
import {
    Notification,
    PagedNotifications,
} from '../../../../shared/types/notifications/Notification';
import { PageDirection } from './notifications.types';
import { UserSessionService } from '../../../../shared/services/user-session.service';
import { catchError, EMPTY, Observable, switchMap } from 'rxjs';
import { DestroyRxComponent } from '../../../../components/destroy-rx/destroy-rx.component';
import { takeUntil } from 'rxjs/operators';
import { DateTime } from 'luxon';
import { NotificationFilter } from 'src/app/shared/enums/notificationFilters';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'las-notifications-dialog',
    templateUrl: './notifications-dialog.component.html',
    styleUrls: ['./notifications-dialog.component.scss'],
    standalone: false,
})
export class NotificationsDialogComponent
    extends DestroyRxComponent
    implements OnInit
{
    timeZone: string;
    loading = true;
    page = 0;
    pageSize = 10;
    total = 0;
    error?: string;
    hasArchivables = false;
    columns = ['comment', 'seen'];
    notifications: Notification[] = [];
    allNotifications: Notification[] = []; // Store all notifications for filtering
    currentFilter: NotificationFilter = NotificationFilter.All;
    selectedTabIndex = 0; // Track selected tab index

    readIcon = {
        color: 'black',
        'margin-right': '1rem',
    };
    commentIcon = {
        color: '#999',
        'font-size': '0.75rem',
        margin: '0px',
        'padding-top': '0.65rem',
    };

    /**
     * Sets the time zone per the local/system time.
     * @param notificationService Service for loading and archiving notifications.
     * @param userSession Session observable to trigger reloading of the notifications on session change.
     * @param data Dialog data containing defaultToAll flag.
     */
    constructor(
        private notificationService: NotificationService,
        private userSession: UserSessionService,
        @Inject(MAT_DIALOG_DATA) public data: { defaultToAll?: boolean },
        private dialogRef: MatDialogRef<NotificationsDialogComponent>,
    ) {
        super();
        this.timeZone = DateTime.local().offsetNameShort;
    }

    close(): void {
        this.dialogRef.close();
    }
    /**
     * Constructs the pipeline to reload the notifications on session change.
     */
    ngOnInit() {
        this.currentFilter = this.data?.defaultToAll
            ? NotificationFilter.All
            : NotificationFilter.Unread;
        this.selectedTabIndex = this.data?.defaultToAll ? 0 : 1; // 0 for All, 1 for Unread
        const notifications$ = this.userSession.session$.pipe(
            // NOTE: We use switchMap to cover the case where a user quickly selects sessions one after another
            //  creating a race condition. This way, the previous http call is canceled.
            switchMap((_) => {
                this.notifications = [];
                return this.getNotifications(
                    undefined,
                    undefined,
                    undefined,
                    this.currentFilter,
                );
            }),
            takeUntil(this.destroy$),
        );
        this.handleResults(notifications$);
    }

    toggleSpinner(loading: boolean): void {
        this.loading = loading;
    }

    /**
     * Starts the loading spinner and invokes the service call to get the paged notifications from the api.
     * @param skip Number of items to skip.
     * @param take Number of items to take.
     * @param reset Flag to indicate if the notifications should be loaded from the cache.
     */
    getNotifications(
        skip = 0,
        take = this.pageSize,
        reset = false,
        filter?: NotificationFilter,
    ): Observable<PagedNotifications> {
        this.toggleSpinner(true);
        return this.notificationService.getNotifications(
            skip,
            take,
            reset,
            filter,
        );
    }

    /**
     * Archives all notifications that have not yet been marked as seen.
     * @param notifications All loaded notifications.
     */
    archiveNotifications(notifications: Notification[]): void {
        this.toggleSpinner(true);
        const ids = notifications
            .filter((notification) => notification.dateSeen === null)
            .map((notification) => notification.notificationID);
        const notifications$ = this.notificationService.archiveNotifications(
            ids,
            this.pageSize,
        );
        this.handleResults(notifications$);
    }

    /**
     * Loads the correct page of notifications when the user clicks the next or previous page button.
     * @param direction The direction to go in.
     */
    handlePagingClick(direction: PageDirection) {
        direction === PageDirection.Left ? (this.page -= 1) : (this.page += 1);
        if (this.page < 0) this.page = 0;
        const skip = this.page * this.pageSize;
        const notifications$ = this.getNotifications(skip, this.pageSize, true);
        this.handleResults(notifications$);
    }

    private handleResults(observable$: Observable<PagedNotifications>): void {
        observable$
            .pipe(catchError((_) => this.handleError()))
            .subscribe((pagedNotifications: PagedNotifications) => {
                this.total = pagedNotifications.total;
                this.populateTable(pagedNotifications.notifications);
            });
    }

    private populateTable(notifications: Notification[]): void {
        this.allNotifications = notifications;
        this.applyFilter();
        this.toggleSpinner(false);
    }

    /**
     * Applies the current filter to the notifications.
     */
    private applyFilter(): void {
        let filteredNotifications = this.allNotifications;

        switch (this.currentFilter) {
            case NotificationFilter.Unread:
                filteredNotifications = this.allNotifications.filter(
                    (n) => n.dateSeen === null,
                );
                break;
            case NotificationFilter.Read:
                filteredNotifications = this.allNotifications.filter(
                    (n) => n.dateSeen !== null,
                );
                break;
            case NotificationFilter.All:
            default:
                filteredNotifications = this.allNotifications;
                break;
        }

        this.notifications = filteredNotifications;
        this.hasArchivables = this.notifications.some(
            (notification) => notification.dateSeen == null,
        );
    }

    /**
     * Changes the notification filter when a tab is selected.
     * @param filter The new filter to apply.
     */
    onFilterChange(filter: NotificationFilter): void {
        this.currentFilter = filter;
        this.applyFilter();
    }

    /**
     * Handles tab change events from the Material tab group.
     * @param event The tab change event.
     */
    onTabChange(event: any): void {
        this.selectedTabIndex = event.index;
        // Reset to first page when switching tabs
        this.page = 0;
        const filters = [
            NotificationFilter.All,
            NotificationFilter.Unread,
            NotificationFilter.Read,
        ];
        const selectedFilter = filters[event.index];
        this.currentFilter = selectedFilter;
        const notifications$ = this.getNotifications(
            0,
            this.pageSize,
            true,
            selectedFilter,
        );
        this.handleResults(notifications$);
    }

    private handleError(): Observable<never> {
        this.error = 'There was a problem loading or saving the notifications.';
        this.loading = false;
        return EMPTY;
    }

    protected readonly PageDirection = PageDirection;
    protected readonly NotificationFilter = NotificationFilter;
}
